/* eslint-disable no-unreachable */
import { useCallback, useContext, useState } from "react";
import { SFXSettings, SettingsContext } from "../../contexts/SettingsContext";
import { playUiSound, UiSoundName } from "../../utils/audio/uiSfx";

export interface IPlayOptions {
  id?: string;
  forceSoundEnabled?: boolean;
  playbackRate?: number;
}

export declare type PlayFunction = (options?: IPlayOptions) => void;

// Sound can only be played ever x millis
const SOUND_FX_RATE_LIMIT_MS = 300;

type ISoundToLastPlayedTimeMillis = {
  [key in SFXSettings | UiSoundName]?: number;
};

// Map legacy sprite names → richer synth sounds where it makes sense, so
// existing call sites still produce distinct sounds. Sites that want a
// specific richer sound (click, whoosh, uplift, settle, thud, warble, pop)
// should pass that name directly via playSound("settle", true), etc.
const LEGACY_TO_UI: Partial<Record<SFXSettings, UiSoundName>> = {
  error: "buzz",
  order_cancelled: "drop",
  order_placed: "ping",
  order_filled: "ding",
  points: "uplift",
  yv_deposit: "uplift",
};

const isUiSoundName = (name: string): name is UiSoundName =>
  name === "ping" ||
  name === "ding" ||
  name === "drop" ||
  name === "settle" ||
  name === "click" ||
  name === "whoosh" ||
  name === "uplift" ||
  name === "pop" ||
  name === "thud" ||
  name === "warble" ||
  name === "buzz";

export const useSFX = () => {
  const [, setSoundRateLimitCounter] = useState<ISoundToLastPlayedTimeMillis>(
    {}
  );

  const { sfx: userSettings } = useContext(SettingsContext);

  const playSound = useCallback(
    (sound: SFXSettings | UiSoundName, force?: boolean) => {
      // Gate against user preference using the closest legacy sprite name so
      // existing on/off settings still apply for the new sounds.
      const gateKey = (isUiSoundName(sound)
        ? Object.entries(LEGACY_TO_UI).find(
            ([, ui]) => ui === sound
          )?.[0]
        : sound) as SFXSettings | undefined;

      if (
        !force &&
        gateKey &&
        gateKey in userSettings &&
        !userSettings.has(gateKey)
      ) {
        return;
      }

      setSoundRateLimitCounter((prev) => {
        const lastPlayed = prev[sound];
        if (
          !lastPlayed ||
          Date.now() >= lastPlayed + SOUND_FX_RATE_LIMIT_MS
        ) {
          try {
            if (isUiSoundName(sound)) {
              // Direct synth call
              playUiSound(sound);
            } else {
              // Prefer the richer synth equivalent if we have one mapped.
              // Unmapped legacy sprites (only `disable_all`, a silent no-op)
              // need no playback — the old use-sound/howler fallback was both
              // unreachable for audible sounds and crashed on mount (Howl.rate
              // on an unloaded sprite), so it has been removed entirely.
              const synthName = LEGACY_TO_UI[sound];
              if (synthName) {
                playUiSound(synthName);
              }
            }
          } catch (error) {
            // Do nothing
          }
        }
        return { ...prev, [sound]: Date.now() };
      });
    },
    [userSettings]
  );

  return {
    playSound,
  };
};
